import { createContext, isValidElement, useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { IconoInfo, IconoAlerta, IconoError, IconoCheck, IconoBuscar } from "./Iconos";
import Boton from "./Boton";
import "./kit.css";

/* ============================================================================
   AVISOS, TOASTS Y ESTADOS DE PANTALLA
   ----------------------------------------------------------------------------
   La regla: el aviso va DONDE ESTÁ EL PROBLEMA.
     · Aviso en línea -> algo que hay que resolver, junto a lo que falla.
     · Toast          -> algo que YA ocurrió, con deshacer si aplica.
   Y los errores dicen qué hacer, no sólo qué falló.

   Esto sustituye a los 66 archivos que hoy resuelven lo mismo con `Swal`, que
   ni respeta el tema ni los tokens.
   ========================================================================== */

const ICONO_POR_TIPO = { info: IconoInfo, aviso: IconoAlerta, error: IconoError, exito: IconoCheck };
export const TIPOS_AVISO = Object.keys(ICONO_POR_TIPO);

export function Aviso({ tipo = "info", children, accion, className = "" }) {
    const seguro = TIPOS_AVISO.includes(tipo) ? tipo : "info";
    const Icono = ICONO_POR_TIPO[seguro];
    return (
        <div className={`kaviso kaviso--${seguro}${className ? ` ${className}` : ""}`} role={seguro === "error" ? "alert" : "status"} data-tipo={seguro} data-testid="aviso">
            <span className="kaviso__icono">
                <Icono size={17} />
            </span>
            <span className="kaviso__texto">{children}</span>
            {accion && (
                <span className="kaviso__accion">
                    {/* Lo normal es declarar la accion como {label, onClick} y
                        que el aviso pinte el boton. Algunas necesitan un boton
                        con estado propio (primario, apagado mientras no se
                        pueda actuar): entonces se pasa el boton ya hecho. */}
                    {isValidElement(accion) ? (
                        accion
                    ) : (
                        <Boton tamano="sm" onClick={accion.onClick}>
                            {accion.label}
                        </Boton>
                    )}
                </span>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ toasts */

const ToastContext = createContext({ mostrar: () => {} });
let contador = 0;

export function ToastProvider({ children, duracion = 4000 }) {
    const [toasts, setToasts] = useState([]);

    const cerrar = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

    const mostrar = useCallback(
        ({ texto, tipo = "exito", accion }) => {
            contador += 1;
            const id = contador;
            setToasts((t) => [...t, { id, texto, tipo, accion }]);
            if (duracion > 0) setTimeout(() => cerrar(id), duracion);
            return id;
        },
        [cerrar, duracion]
    );

    const valor = useMemo(() => ({ mostrar, cerrar }), [mostrar, cerrar]);

    return (
        <ToastContext.Provider value={valor}>
            {children}
            {toasts.length > 0 &&
                createPortal(
                    <div className="ktoast-zona" data-testid="toast-zona">
                        {toasts.map((t) => {
                            const Icono = ICONO_POR_TIPO[t.tipo] || IconoCheck;
                            return (
                                <div className="ktoast" key={t.id} role="status" data-testid="toast">
                                    <span className={`ktoast__icono--${t.tipo === "error" ? "error" : "exito"}`}>
                                        <Icono size={16} grosor={2.4} />
                                    </span>
                                    <span>{t.texto}</span>
                                    {t.accion && (
                                        <button
                                            type="button"
                                            className="ktoast__accion"
                                            onClick={() => {
                                                t.accion.onClick();
                                                cerrar(t.id);
                                            }}
                                        >
                                            {t.accion.label}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>,
                    document.body
                )}
        </ToastContext.Provider>
    );
}

export function useToast() {
    return useContext(ToastContext);
}

/* ------------------------------------------------------ estados de pantalla */

/**
 * Vacío. Distingue las dos situaciones que el sistema mezclaba: "no hay
 * resultados con estos filtros" (hay datos, el filtro los esconde) y "todavía
 * no hay datos". La primera ofrece limpiar; la segunda, no.
 */
export function EstadoVacio({ conFiltros = false, titulo, texto, onLimpiar, icono: Icono = IconoBuscar, accion }) {
    const tituloFinal = titulo || (conFiltros ? "Nada coincide con estos filtros" : "Todavía no hay datos");
    return (
        <div className="kvacio" data-testid="estado-vacio" data-con-filtros={conFiltros}>
            <span className="kvacio__icono">
                <Icono size={22} />
            </span>
            <div className="kvacio__titulo">{tituloFinal}</div>
            {texto && <div className="kvacio__texto">{texto}</div>}
            {conFiltros && onLimpiar && <Boton onClick={onLimpiar}>Limpiar filtros</Boton>}
            {!conFiltros && accion && (
                <Boton variante="primario" onClick={accion.onClick}>
                    {accion.label}
                </Boton>
            )}
        </div>
    );
}

export default Aviso;
